import { AssetRef, Resource, VideoModel } from '../types';
import { MediaKind, mediaKindOfRef, mediaKindOfResource } from './assetTokens';

// 与后端 internal/service/video/limits.go 的三个常量保持一致。
export const DEFAULT_MIN_DURATION_SEC = 4;
export const DEFAULT_MAX_DURATION_SEC = 15;
export const MAX_CONFIGURABLE_DURATION_SEC = 60;

// 与后端 handler_shot_video.go 的空值回退保持一致，否则前端放行、后端拒绝。
const FALLBACK_PLATFORM = 'moyu-jimeng';
const FALLBACK_MODEL = 'seedance-2.0-pro';

export interface VideoLimits {
  minDurationSec: number;
  maxDurationSec: number;
  maxRefImages: number;
  maxRefVideos: number;
  maxRefAudios: number;
  maxRefTotal: number;
}

// resolved=false 表示项目或模型列表尚未加载/加载失败/匹配不到，
// 调用方必须放行（数量与时长都放行），后端是唯一权威闸口。
export interface ResolvedVideoLimits {
  limits: VideoLimits;
  resolved: boolean;
}

export const DEFAULT_LIMITS: Readonly<VideoLimits> = {
  minDurationSec: DEFAULT_MIN_DURATION_SEC,
  maxDurationSec: DEFAULT_MAX_DURATION_SEC,
  maxRefImages: 0,
  maxRefVideos: 0,
  maxRefAudios: 0,
  maxRefTotal: 0,
};

export type LimitKind = 'images' | 'videos' | 'audios' | 'total';

export interface RefCounts {
  images: number;
  videos: number;
  audios: number;
  total: number;
}

export const EMPTY_COUNTS: Readonly<RefCounts> = { images: 0, videos: 0, audios: 0, total: 0 };

const positiveOr = (value: number | undefined, fallback: number): number =>
  typeof value === 'number' && value > 0 ? value : fallback;

export function resolveVideoLimits(
  models: VideoModel[],
  project?: { videoPlatform?: string; videoModel?: string } | null,
): ResolvedVideoLimits {
  // project 未加载时不能套用空值回退：FALLBACK_* 是真实存在的默认模型，会匹配成功
  // 并拿别的模型的限制去拦用户。后端的同名回退只在 project.VideoPlatform 为空串
  // （项目真的没配）时触发。
  if (!project) return { limits: { ...DEFAULT_LIMITS }, resolved: false };
  const targetPlatform = project.videoPlatform || FALLBACK_PLATFORM;
  const targetModel = project.videoModel || FALLBACK_MODEL;
  const found = models.find(m => m.platform === targetPlatform && m.model === targetModel);
  if (!found) return { limits: { ...DEFAULT_LIMITS }, resolved: false };
  return {
    resolved: true,
    limits: {
      // 纵深防御，与后端 LimitsOf 的两步夹取一一对应（回退之后再夹）：配置无论来自
      // 何处（含直连 DB 改数据）都必须落在 provider 的截断区间内，否则前端给出的
      // 区间比后端宽，会出现"前端放行、后端报错"。夹取只收紧不放宽，不会误拦。
      minDurationSec: Math.max(positiveOr(found.minDurationSec, DEFAULT_MIN_DURATION_SEC), DEFAULT_MIN_DURATION_SEC),
      maxDurationSec: Math.min(positiveOr(found.maxDurationSec, DEFAULT_MAX_DURATION_SEC), MAX_CONFIGURABLE_DURATION_SEC),
      maxRefImages: positiveOr(found.maxRefImages, 0),
      maxRefVideos: positiveOr(found.maxRefVideos, 0),
      maxRefAudios: positiveOr(found.maxRefAudios, 0),
      maxRefTotal: positiveOr(found.maxRefTotal, 0),
    },
  };
}

export function countKinds(kinds: MediaKind[]): RefCounts {
  const images = kinds.filter(k => k === 'image').length;
  const videos = kinds.filter(k => k === 'video').length;
  const audios = kinds.filter(k => k === 'audio').length;
  return { images, videos, audios, total: images + videos + audios };
}

export function addCounts(a: RefCounts, b: RefCounts): RefCounts {
  return {
    images: a.images + b.images,
    videos: a.videos + b.videos,
    audios: a.audios + b.audios,
    total: a.total + b.total,
  };
}

// 计数口径必须与后端 mediaRefs 的构建一致：
//   - AssetRef：source === 'shot_ref' 归为 video（mediaKindOfRef，与后端
//     promptgen.MediaTypeOf 同规则），其余归 image；URL 未解析的不计数（后端的
//     missingAssets 会单独拦下）；
//   - Resource：一律按自身 type（mediaKindOfResource）。**shot_ref 的分类规则只对
//     AssetRef 生效，对 Resource 不生效** —— createRefResource 建 Resource、
//     addShotAssetRef 建 AssetRef，两条通路不相交，不会重复计数；
//   - preset_audio 的 Resource 一律不计入：非预审平台后端根本不挂它，而前端拿不到
//     平台能力表也不该复制那份列表。少算的方向是“前端放行、后端拒绝”，不会误伤。
export function countShotRefs(refs: AssetRef[], resources: Resource[]): RefCounts {
  const kinds: MediaKind[] = [
    ...refs.filter(ref => Boolean(ref.imageUrl)).map(mediaKindOfRef),
    ...resources.filter(res => res.source !== 'preset_audio').map(mediaKindOfResource),
  ];
  return countKinds(kinds);
}

// 返回第一个被突破的限制项，全部满足返回 null。
// 顺序与后端 Limits.Validate 一致：图片 → 视频 → 音频 → 总数。
// 刻意不提供 isWithin(limit, value) 这类双 number 参数的 API——写反后编译通过、
// 语义反转，全靠人眼。
export function firstExceededLimit(
  limits: VideoLimits,
  counts: RefCounts,
): { kind: LimitKind; limit: number; actual: number } | null {
  const checks: Array<{ kind: LimitKind; limit: number; actual: number }> = [
    { kind: 'images', limit: limits.maxRefImages, actual: counts.images },
    { kind: 'videos', limit: limits.maxRefVideos, actual: counts.videos },
    { kind: 'audios', limit: limits.maxRefAudios, actual: counts.audios },
    { kind: 'total', limit: limits.maxRefTotal, actual: counts.total },
  ];
  return checks.find(c => c.limit > 0 && c.actual > c.limit) ?? null;
}
